import React, { useState } from "react";
import { X } from "lucide-react";
import { useStore } from "../store";
import { EIGHT_D_STEPS, eightDStatusStyle } from "../data/eightDData";
import { useEightDs, useFmeas, apiPatchFmea } from "../api/sitroom";
import { useQueryClient } from "@tanstack/react-query";
import { scoreFmeaRpn, generateFmeaFrom8D } from "../api/fmea";
import { RoleGuard } from "./RoleGuard";

const SectionTitle = ({ children }) => (
  <div
    className="mt-5 mb-2.5"
    style={{
      fontFamily: "'DM Mono', monospace",
      fontSize: 10,
      letterSpacing: "1.6px",
      color: "#9198b0",
      textTransform: "uppercase",
    }}
  >
    {children}
  </div>
);

const Pill = ({ children, color }) => (
  <span
    style={{
      background: `${color}22`,
      border: `1px solid ${color}80`,
      color,
      fontFamily: "'DM Mono', monospace",
      fontSize: 9,
      padding: "2px 7px",
      borderRadius: 3,
      letterSpacing: "1px",
      fontWeight: 500,
    }}
  >
    {children}
  </span>
);

const DStepCard = ({ code, label, status, children, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const color =
    status === "complete" ? "#27ae60" : status === "active" ? "#185FA5" : "#9198b0";
  return (
    <div
      className="rounded-md mb-2"
      style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.06)" }}
    >
      <button
        className="w-full flex items-center gap-3 p-3"
        onClick={() => setOpen(!open)}
        style={{ textAlign: "left" }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: status === "complete" ? "#27ae60" : "transparent",
            border: `2px solid ${color}`,
            color: status === "complete" ? "#fff" : color,
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            fontWeight: 600,
            textAlign: "center",
            lineHeight: "24px",
            boxShadow: status === "active" ? `0 0 8px ${color}80` : "none",
          }}
        >
          {code}
        </span>
        <span style={{ flex: 1, fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 13, color: "#f0f2f8" }}>
          {label}
        </span>
        <Pill color={color}>
          {status === "complete" ? "COMPLETE" : status === "active" ? "IN PROGRESS" : "NOT STARTED"}
        </Pill>
        <span style={{ color: "#9198b0", fontSize: 14 }}>{open ? "▾" : "▸"}</span>
      </button>
      {open && children && (
        <div className="px-3 pb-3" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          {children}
        </div>
      )}
    </div>
  );
};

const FieldRow = ({ label, value }) => (
  <div className="mt-2">
    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase" }}>
      {label}
    </div>
    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#cfd1d8", marginTop: 2, lineHeight: 1.5 }}>
      {value || "—"}
    </div>
  </div>
);

const MiniTable = ({ rows, columns }) => (
  <div className="mt-2 overflow-x-auto">
    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'DM Mono', monospace", fontSize: 10 }}>
      <thead>
        <tr style={{ color: "#9198b0" }}>
          {columns.map((c) => (
            <th key={c} className="text-left py-1.5 px-2" style={{ letterSpacing: "1px" }}>
              {c.toUpperCase()}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(rows || []).map((r, i) => (
          <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.05)", color: "#cfd1d8" }}>
            {columns.map((c) => (
              <td key={c} className="py-1.5 px-2">{r[c.toLowerCase().replace(/\s/g, "")] || r[c] || ""}</td>
            ))}
          </tr>
        ))}
        {(!rows || rows.length === 0) && (
          <tr><td colSpan={columns.length} className="py-2 px-2" style={{ color: "#9198b0" }}>No entries yet.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export const EightDPanel = () => {
  const isOpen = useStore((s) => s.isEightDPanelOpen);
  const closePanel = useStore((s) => s.closeEightDPanel);
  const selectedId = useStore((s) => s.selectedEightDId);
  const openIncident = useStore((s) => s.openPanel);
  const addToast = useStore((s) => s.addToast);
  const { data: eightDReports = [] } = useEightDs();
  const { data: fmeas = [] } = useFmeas();
  const qc = useQueryClient();
  const updateFmeaForIncident = async (incidentId, patch) => {
    const target = fmeas.find((f) => f.sourceIncident === incidentId);
    if (!target) return;
    await apiPatchFmea(target.id, patch);
    qc.invalidateQueries({ queryKey: ["fmeas"] });
  };

  const r = eightDReports.find((x) => x.id === selectedId) || eightDReports[0];
  if (!isOpen || !r) return null;

  const st = eightDStatusStyle(r.status);
  const canClose = r.progress >= 5; // D4 + D5 complete = step index >=5

  const handleClose8D = () => {
    addToast({
      type: "info",
      title: "8D closed — generating FMEA suggestion…",
      body: `Synthesizing D4 root cause + D5 PCAs of ${r.id} via Claude Sonnet`,
    });
    const partiesStr = (r.d1 || [])
      .map((m) => `${m.name} (${m.role})`)
      .join(", ");
    const d5Problem = (r.d5?.problem || [])
      .map((x) => `${x.action} — ${x.responsible} — verified by ${x.effect}`)
      .join("; ");
    const d5NonDetection = (r.d5?.nonDetection || [])
      .map((x) => `${x.action} — ${x.responsible} — verified by ${x.effect}`)
      .join("; ");

    // Fire AI-generation of the FMEA action AND AI-scoring of RPN in parallel
    (async () => {
      try {
        const fmeaGen = await generateFmeaFrom8D({
          eightDId: r.id,
          failureTitle: r.title,
          d4RootCause: r.d4?.finalRootCause || "",
          d4EscapeRoot: r.d4?.finalEscapeRoot || "",
          d5CorrectiveActions: d5Problem,
          d5NonDetectionActions: d5NonDetection,
          sourceIncident: r.sourceIncident,
          affectedParties: partiesStr,
        });
        updateFmeaForIncident(r.sourceIncident, {
          recommendedAction: fmeaGen.action,
          aiGenerated: true,
          source8D: r.id,
          generatedAt: new Date().toISOString().slice(0, 16).replace("T", " "),
        });
        addToast({
          type: "info",
          title: "FMEA suggestion ready",
          body: `Claude Sonnet drafted from ${r.id}`,
        });
      } catch (_) {
        addToast({
          type: "info",
          title: "FMEA AI draft unavailable",
          body: "Manual editing still available",
        });
      }
    })();
    // Fire AI auto-scoring in the background — no await, no UI block
    (async () => {
      try {
        const ai = await scoreFmeaRpn({
          failureMode: r.title,
          failureEffect: r.d2?.complaint || r.title,
          potentialCause: r.d4?.finalRootCause || "",
          currentControls: (r.d3?.customer || []).map((x) => x.action).join("; ") || "Manual checks",
          incidentSeverity: "S1",
          productionImpact: r.d2?.howMany || "",
        });
        addToast({
          type: "info",
          title: "AI auto-scored RPN",
          body: `S${ai.sev}·O${ai.occ}·D${ai.det} for ${r.id}`,
        });
      } catch (_) {
        // silent fallback — manual scoring still available
      }
    })();
    setTimeout(() => {
      addToast({
        type: "info",
        title: `Review FMEA suggestion for ${r.id}`,
        body: "Open the FMEA module to approve",
        action: {
          label: "View →",
          onClick: () => {
            closePanel();
            window.location.href = "/fmea";
          },
        },
      });
    }, 1200);
    closePanel();
  };

  const stepStatus = (i) => (i < r.progress ? "complete" : i === r.progress ? "active" : "pending");

  return (
    <>
      <div
        className="fixed inset-0 z-[400] backdrop-enter"
        style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}
        onClick={closePanel}
        data-testid="8d-panel-backdrop"
      />
      <aside
        className="fixed top-0 right-0 bottom-0 z-[401] panel-enter flex flex-col"
        style={{ width: "min(600px, 100vw)", background: "#0d0f18", borderLeft: "1px solid rgba(255,255,255,0.1)" }}
        data-testid="8d-panel"
      >
        <header className="relative p-5 pb-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
          <button
            onClick={closePanel}
            className="absolute right-4 top-4 flex items-center justify-center rounded-md hover:bg-white/5"
            style={{ width: 28, height: 28, border: "1px solid rgba(255,255,255,0.1)", color: "#9aa1b3" }}
            data-testid="close-8d-panel-btn"
            aria-label="Close"
          >
            <X size={14} />
          </button>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9ec3ec" }}>{r.id}</span>
            <Pill color="#185FA5">8D</Pill>
            <Pill color={st.color}>{st.label}</Pill>
          </div>
          <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 16, color: "#f0f2f8", paddingRight: 40, lineHeight: 1.35 }}>
            {r.title}
          </div>
          <button
            onClick={() => openIncident(r.sourceIncident)}
            className="mt-1"
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9ec3ec", textDecoration: "underline" }}
            data-testid="8d-source-incident-link"
          >
            ↗ {r.sourceIncident}
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 pt-3">
          <div className="flex items-center gap-3 mb-3">
            {EIGHT_D_STEPS.map((s, i) => {
              const ss = stepStatus(i);
              const c = ss === "complete" ? "#27ae60" : ss === "active" ? "#185FA5" : "rgba(255,255,255,0.15)";
              return (
                <div key={s.code} className="flex-1 flex items-center gap-1">
                  <span
                    style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: ss === "complete" ? c : "transparent",
                      border: `1.5px solid ${c}`,
                      boxShadow: ss === "active" ? `0 0 6px ${c}` : "none",
                    }}
                  />
                  {i < 8 && <div className="flex-1 h-px" style={{ background: ss === "complete" ? "#27ae60" : "rgba(255,255,255,0.08)" }} />}
                </div>
              );
            })}
          </div>

          <SectionTitle>D-Steps</SectionTitle>

          <DStepCard code="D0" label="Emergency Response Action" status={stepStatus(0)} defaultOpen={false}>
            <FieldRow label="By Customer (3.1)" value={r.d0?.customer?.action} />
            <FieldRow label="By Supplier (3.2)" value={r.d0?.supplier?.action} />
          </DStepCard>

          <DStepCard code="D1" label="Team" status={stepStatus(1)}>
            <MiniTable rows={r.d1} columns={["Role", "Name", "Function"]} />
          </DStepCard>

          <DStepCard code="D2" label="Problem Description" status={stepStatus(2)}>
            <FieldRow label="Complaint" value={r.d2?.complaint} />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <FieldRow label="What" value={r.d2?.what} />
              <FieldRow label="When" value={r.d2?.when} />
              <FieldRow label="How Many" value={r.d2?.howMany} />
              <FieldRow label="How Big" value={r.d2?.howBig} />
              <FieldRow label="Where" value={r.d2?.where} />
            </div>
          </DStepCard>

          <DStepCard code="D3" label="Interim Containment (ICA)" status={stepStatus(3)}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8", marginTop: 4 }}>
              To isolate effects of the problem until Permanent Corrective Actions are implemented
            </div>
            <FieldRow label="Customer Actions" value={(r.d3?.customer || []).map((x) => `${x.action} (${x.responsible})`).join("; ")} />
            <FieldRow label="Supplier Actions" value={(r.d3?.supplier || []).map((x) => `${x.action} (${x.responsible})`).join("; ")} />
            <FieldRow label="Affected Products" value={r.d3?.affectedProducts} />
          </DStepCard>

          <DStepCard code="D4" label="Root Cause Analysis" status={stepStatus(4)} defaultOpen={true}>
            <FieldRow label="Possible Roots Investigated" value={r.d4?.possibleRoots} />
            <FieldRow label="5 Why — Problem path" value={(r.d4?.whyProblem || []).join(" → ")} />
            <FieldRow label="Final Root Cause" value={r.d4?.finalRootCause} />
            <FieldRow label="5 Why — Non-detection path" value={(r.d4?.whyEscape || []).join(" → ")} />
            <FieldRow label="Final Escape Root" value={r.d4?.finalEscapeRoot} />
            <FieldRow label="Category" value={(r.d4?.category || []).join(", ")} />
          </DStepCard>

          <DStepCard code="D5" label="Choose & Verify PCA" status={stepStatus(5)} defaultOpen={true}>
            <FieldRow label="Problem PCAs" value={(r.d5?.problem || []).map((x) => `${x.action} (${x.responsible} · ${x.effect})`).join("; ")} />
            <FieldRow label="Non-Detection PCAs" value={(r.d5?.nonDetection || []).map((x) => `${x.action} (${x.responsible} · ${x.effect})`).join("; ")} />
          </DStepCard>

          <DStepCard code="D6" label="Implement & Validate PCA" status={stepStatus(6)} />
          <DStepCard code="D7" label="Prevent Recurrence" status={stepStatus(7)} />
          <DStepCard code="D8" label="Closing" status={stepStatus(8)}>
            {r.d8 ? (
              <>
                <FieldRow label="Supplier Credits" value={r.d8.supplierCredits} />
                <FieldRow label="Approved By (Supplier)" value={`${r.d8.supplierApprovedBy} · ${r.d8.supplierDate}`} />
                <FieldRow label="Customer Approved" value={`${r.d8.customerApprovedBy} · ${r.d8.customerDate}`} />
                <FieldRow label="Share Best Practices" value={r.d8.shareBestPractices ? "Yes" : "No"} />
              </>
            ) : (
              <FieldRow label="Status" value="Pending closure" />
            )}
          </DStepCard>
        </div>

        <footer
          className="flex items-center gap-2 p-3 flex-wrap"
          style={{ background: "#0d0f18", borderTop: "1px solid rgba(255,255,255,0.12)" }}
        >
          <button
            className="px-3 py-2 rounded-md"
            style={{
              background: "transparent", border: "1px solid rgba(255,255,255,0.12)", color: "#cfd1d8",
              fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 600, fontSize: 11,
            }}
            data-testid="8d-save-btn"
          >
            Save Draft
          </button>
          <button
            className="px-3 py-2 rounded-md"
            style={{
              background: "transparent", border: "1px solid rgba(255,255,255,0.12)", color: "#cfd1d8",
              fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 600, fontSize: 11,
            }}
            data-testid="8d-export-pdf-btn"
          >
            Export PDF
          </button>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0", letterSpacing: "0.5px" }}>
            Step {r.currentStep} of 8
          </span>
          <div className="flex-1" />
          <RoleGuard minRole="escalation_manager">
            <button
              disabled={!canClose}
              onClick={handleClose8D}
              className="px-3 py-2 rounded-md"
              style={{
                background: canClose ? "rgba(39,174,96,0.18)" : "rgba(39,174,96,0.06)",
                border: `1px solid rgba(39,174,96,${canClose ? 0.5 : 0.2})`,
                color: canClose ? "#4ed084" : "#3d6048",
                fontFamily: "'Syne', system-ui, sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.4px",
                cursor: canClose ? "pointer" : "not-allowed",
              }}
              data-testid="8d-close-generate-fmea-btn"
            >
              ✓ Close 8D & Generate FMEA
            </button>
          </RoleGuard>
        </footer>
      </aside>
    </>
  );
};
